<template>
    <section class="profile-image-upload-">
        <div class="title">{{ $t('profile.profileImage.modalTitle') }}</div>

        <div class="image">
            <img v-show="showImage"
                :src="profilePic"
                @load="picLoaded"
            >

            <div v-if="showCropper"
                class="cropper"
            >
                <cropper ref="cropper"
                    :src="profilePic"
                    class="cropper__upload"
                    :stencil-props="{
                        handlers: {},
                        movable: false,
                        resizable: false,
                        scalable: false,
                        aspectRatio: 1,
                    }"
                    :resize-image="{
                        adjustStencil: false,
                    }"
                    image-restriction="stencil"
                />

                <div class="cropper__options">
                    <i class="cropper__options-option icon-zoom-in"
                        @click="zoom(2)"
                    />

                    <i class="cropper__options-option icon-zoom-out"
                        @click="zoom(0.5)"
                    />

                    <i class="cropper__options-option icon-rotate"
                        @click="rotate(90)"
                    />
                </div>
            </div>

            <loading-spinner v-show="isLoading"
                class="image__loader"
            />
        </div>

        <div class="actions">
            <div id="action-upload"
                :class="['actions__item', {'actions__item--disabled': isLoading}]"
                @click="uploadView()"
            >
                <i class="icon-upload" />
                {{ $t('profile.profileImage.upload') }}
            </div>

            <div id="action-delete"
                :class="['actions__item', {'actions__item--disabled': isLoading || isActionDisabled}]"
                @click="deleteView()"
            >
                <i class="icon-delete" />
                {{ $t('profile.profileImage.delete') }}
            </div>
        </div>

        <div class="buttons">
            <div v-if="isDeleteView"
                id="button-delete"
                :class="'buttons__item buttons__item--error'"
                @click="deletePic"
            >
                {{ $t('profile.profileImage.deleteConfirmOk') }}
            </div>

            <div v-else
                id="button-save"
                :class="['buttons__item buttons__item--success', {'buttons__item--disabled': isSaveDisabled}]"
                @click="save"
            >
                {{ $t('global.button.save') }}
            </div>

            <div id="button-cancel"
                class="buttons__item"
                @click="cancelAction"
            >
                {{ $t('global.button.cancel') }}
            </div>
        </div>

        <form ref="uploadForm"
            class="upload-form"
        >
            <input ref="uploadInput"
                type="file"
                accept=".png,.jpg,.jpeg,.webp"
                @change="onAvatarPicked"
            >
        </form>
    </section>
</template>

<script>
import {
    mapActions,
    mapGetters,
} from 'vuex';

// 3rd party
import {
    Cropper,
} from 'vue-advanced-cropper';

import 'vue-advanced-cropper/dist/style.css';

// Components
import loadingSpinner from '@webapp/components/loading-spinner/loading-spinner.vue';

// Shared Modules
import sharedEpicTypes from '@shared/epics/types.js';

// Local Types
import localEpicTypes from '@webapp/store/types.js';

export default {
    name: 'profile-image-upload-modal',
    components: {
        loadingSpinner,
        Cropper,
    },
    data() {
        return {
            base64: '',
            loading: true,
            profilePic: '',
            oldPic: '',
            defaultPic: require('@webapp/assets/profile-blank.webp'),
            isLoading: true,
            isDeleteView: false,
            isCropperView: false,
        };
    },
    computed: {
        ...mapGetters([
            'modal',
            'userAvatar',
        ]),
        isSaveDisabled() {
            return !this.isCropperView;
        },
        isActionDisabled() {
            return !this.userAvatar;
        },
        cancelAction() {
            return this.isDeleteView ? this.cancelDelete : this.cancel;
        },
        image() {
            return this.userAvatar || this.defaultPic;
        },
        showImage() {
            return !this.isLoading && !this.isCropperView;
        },
        showCropper() {
            return !this.isLoading && this.isCropperView;
        },
    },
    watch: {
        'userAvatar'(val) {
            if (val) {
                this.profilePic = val;
                this.$notify({
                    type: 'success',
                    text: this.$t('profile.profileImage.successfulUpload'),
                });

                this.resetFileUpload();
            } else {
                this.isDeleteView = false;
                this.profilePic = this.defaultPic;
            }

            this.isCropperView = false;
            this.isLoading = false;
        },
    },
    created() {
        this.profilePic = this.image;
    },
    methods: {
        ...mapActions({
            uploadAvatar: sharedEpicTypes.UPDATE_USER_AVATAR,
            deleteUserAvatar: sharedEpicTypes.DELETE_USER_AVATAR,
            resetModal: localEpicTypes.RESET_MODAL,
        }),
        picLoaded() {
            this.isLoading = false;
        },
        uploadView() {
            this.isDeleteView = false;
            this.$refs.uploadInput.click();
        },
        deleteView() {
            this.isCropperView = false;
            this.isDeleteView = true;
        },
        onAvatarPicked(e) {
            this.isLoading = true;
            const files = e.target.files;

            if (files[0]) {
                const fr = new FileReader();

                fr.readAsDataURL(files[0]);
                fr.addEventListener('load', () => {
                    this.oldPic = this.profilePic;
                    this.profilePic = fr.result;
                    this.isLoading = false;
                    this.isCropperView = true;
                });
            } else {
                this.isLoading = false;
            }
        },
        resetFileUpload() {
            this.base64 = '';
            this.$refs.uploadForm.reset();
        },
        cancel() {
            if (this.base64) {
                this.resetFileUpload();
            }

            this.resetModal();
        },
        save() {
            this.isLoading = true;

            const result = this.$refs.cropper.getResult();

            this.base64 = result.canvas.toDataURL('image/jpeg').split(',')[1];

            this.uploadAvatar({
                base64: this.base64,
            });
        },
        deletePic() {
            this.isLoading = true;
            this.deleteUserAvatar();
        },
        cancelDelete() {
            this.isDeleteView = false;
        },
        zoom(factor) {
            this.$refs.cropper.zoom(factor);
        },
        rotate(angle) {
            this.$refs.cropper.rotate(angle);
        },
    },
};
</script>

<style lang="scss" scoped>
.title {
    text-align: center;
    font-size: 1.7rem;
    line-height: 1.7rem;
    white-space: pre-line;
    color: $cyprus-color;
    margin-bottom: 4rem;
}

.image {
    margin: auto;
    display: block;
    text-align: center;

    img,
    &__loader {
        height: 24rem;
        width: 24rem;
        border-radius: 50%;
    }
}

.actions {
    display: flex;
    justify-content: center;
    margin: 5rem 0;

    &__item {
        display: flex;
        flex-direction: column;
        align-items: center;
        font-size: 1.4rem;
        line-height: 2.2rem;
        color: $daintree-color;
        margin: 0 4.1rem;
        cursor: pointer;

        i {
            font-size: 2.3rem;
            padding-bottom: 0.5rem;
        }

        &--disabled {
            pointer-events: none;
            opacity: 0.4;
        }
    }
}

.buttons {
    display: flex;
    align-items: center;
    flex-direction: column;
    margin: 0 0 2rem;
    width: 100%;

    &__item {
        text-align: center;
        font-size: 1.5rem;
        line-height: 1.5rem;
        width: 34rem;
        margin: 2rem 0 0;
        cursor: pointer;

        @include max-width(sm) {
            width: 25rem;
        }

        &--success,
        &--error {
            margin-top: 0;
            line-height: 4rem;
            border-radius: 0.4rem;
            color: $white-color;
        }

        &--success {
            background: $lima-color;
            color: $daintree-color;
        }

        &--error {
            background: $monza-color;
        }

        &--disabled {
            pointer-events: none;
            opacity: 0.4;
        }
    }
}

.upload-form {
    display: none;
}
</style>